import { useQuery } from "@tanstack/react-query";
import { getVideoPlayback, getVideos, VideoMeta, VideoPlayback } from "../../../api/videoApi";

export const VIDEOS_QUERY_KEY = ["videos"];
export const videoPlaybackQueryKey = (videoId: number) => ["video-playback", videoId];

// GET /video's own response is deliberately playback-URL-free so it stays cacheable server-side
// (see cedar-backend's VideoMapSchema docstring) - this hook makes the resulting two-step fetch
// (look up a video type's id, then its own short-lived signed playback_url) transparent to
// whichever component just wants a URL it can drop straight into a <video> element. Shared by
// every video-type-specific hook (useQuickTourVideo, GoogleConnectTileContent's own usage) rather
// than each reimplementing this same two-step lookup.
export const useVideoByType = (videoType: string) => {
  const { data: videos, isLoading: isLoadingVideos } = useQuery<Record<string, VideoMeta>>(VIDEOS_QUERY_KEY, getVideos, {
    // The "latest video per type" map only changes when someone uploads a new one via the
    // admin-only POST /video, not something any caller needs to poll for.
    staleTime: Infinity,
  });

  const videoId = videos?.[videoType]?.id;

  const { data: playback, isLoading: isLoadingPlayback } = useQuery<VideoPlayback>(
    videoPlaybackQueryKey(videoId ?? -1),
    () => getVideoPlayback(videoId as number),
    {
      // Only fires once the id lookup above has resolved - videoId is a required path param on
      // the backend route, so there's nothing to fetch before then.
      enabled: videoId !== undefined,
      // playback_url is a signed URL with a real server-side expiry (~1hr) - never treat one
      // fetched earlier in a long-lived session/tab as still fresh (still refetches on every
      // genuine remount, e.g. navigating back to this page).
      staleTime: 0,
      // staleTime: 0 above means this query is always "stale", so react-query's own default
      // (refetchOnWindowFocus: true, app-wide - see App.tsx's plain `new QueryClient()`) was
      // refetching it - producing a brand-new signed URL string - every time the browser tab
      // regained focus, even mid-playback. Changing `src` on a mounted <video> makes the browser
      // reload it from scratch, which reset playback to 0 any time someone merely switched away
      // to another tab and back. Disabled here the same way GeoReportingFilters.tsx already does
      // for its own always-stale query.
      refetchOnWindowFocus: false,
    }
  );

  return {
    playbackUrl: playback?.playback_url,
    isLoading: isLoadingVideos || (videoId !== undefined && isLoadingPlayback),
  };
};
